/**
 * useRelationshipEditMode - Handles relationship edit mode highlighting and click handlers
 */
import { useEffect } from 'react'
import { dia } from '@joint/core'
import type { RelationshipEditState } from '../types'
import { TemplateRelationType } from '../../../../../map-editor-entities/schema/templateSchema'
import { applyRelationshipEditHighlights, createEditModeClickHandler } from '../handlers/relationshipEditHandlers'

interface UseRelationshipEditModeParams {
  graph: dia.Graph | null
  paper: dia.Paper | null
  relationshipEditState: RelationshipEditState
  selectedElementId: string | null
  mutableRelationTypes: Record<string, TemplateRelationType>
  handleObjectUpdate: (id: string, updates: Partial<any>) => void
  setRelationshipEditState: React.Dispatch<React.SetStateAction<RelationshipEditState>>
}

export function useRelationshipEditMode({
  graph,
  paper,
  relationshipEditState,
  selectedElementId,
  mutableRelationTypes,
  handleObjectUpdate,
  setRelationshipEditState,
}: UseRelationshipEditModeParams) {
  // Highlight available targets when in relationship edit mode
  useEffect(() => {
    return applyRelationshipEditHighlights(graph, paper, relationshipEditState)
  }, [relationshipEditState, graph, paper])

  // Handle canvas clicks when in relationship edit mode
  useEffect(() => {
    if (!paper || !graph) return
    if (!relationshipEditState.editing || !relationshipEditState.propertyKey || !selectedElementId) return

    const handleEditModeClick = createEditModeClickHandler({
      graph,
      paper,
      relationshipEditState,
      selectedElementId,
      mutableRelationTypes,
      handleObjectUpdate,
      setRelationshipEditState,
    })

    paper.on('element:pointerclick', handleEditModeClick)

    return () => {
      paper.off('element:pointerclick', handleEditModeClick)
    }
  }, [
    paper,
    graph,
    relationshipEditState,
    selectedElementId,
    mutableRelationTypes,
    handleObjectUpdate,
    setRelationshipEditState,
  ])
}
